import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Tab,
  Tabs,
  Tooltip,
  TablePagination,
  TableHead,
  TableBody,
  TableRow,
  useMediaQuery,
  useTheme,
  Switch,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';
import { useParams } from 'react-router-dom';

import { exportToCsv } from './export-csv';
import ProjectCard from './project-card';
import ProjectName from './project-name';
import ProjectRowActions from './project-row-actions';
import SortButton from './sort-button';
import SortMenu from './sort-menu';
import {
  formatWorkflowStatus,
  ASSESSMENT_STATUS_COLUMNS,
} from './workflow-status-labels';
import { useSnackbarContext } from '../base/snackbar';
import { StyledTableCell, StyledTableRow } from '../base/table/generic-table';
import Text from '../base/text';
import { useViewSwitcher } from '../base/view-switcher';
import ChampionIcon from '../project/champion-icon';
import { useAIResourcesContext } from '../project/dashboard/ai-resources-context';

import Button from '~/client/components/base/button';
import Card from '~/client/components/base/card';
import Spacer, { spc } from '~/client/components/base/spacer';
import ProgressWithText from '~/client/components/progress-with-text';
import { usePermissions } from '~/client/contexts/permissions-context';
import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';
import { calculateProjectTableColumns } from '~c/components/assessment/helper';
import Arranger from '~c/components/base/arranger';
import StatusBadge, { AlignmentLevel } from '~c/components/base/status-badge';

// Maps a project risk status to a compliance level for the PROJECT STATUS badge.
const toAlignmentLevel = (
  status: GQL.AssessmentRiskStatusType | null | undefined
): AlignmentLevel => {
  switch (status) {
    case GQL.AssessmentRiskStatusType.Low:
      return 'fully-compliant';
    case GQL.AssessmentRiskStatusType.Medium:
      return 'partially-compliant';
    case GQL.AssessmentRiskStatusType.High:
      return 'not-compliant';
    case GQL.AssessmentRiskStatusType.Unavailable:
    default:
      return 'unavailable';
  }
};

// Component to display assessment category status
interface AssessmentCategoryStatusProps {
  status: 'N_A' | 'INCOMPLETE' | 'PASS' | 'FAIL' | undefined;
}

const AssessmentCategoryStatus: React.FC<AssessmentCategoryStatusProps> = ({
  status,
}) => {
  switch (status) {
    case 'PASS':
      return (
        <Tooltip title="Pass">
          <CheckIcon sx={{ color: 'success.main' }} />
        </Tooltip>
      );
    case 'FAIL':
      return (
        <Tooltip title="Fail">
          <CloseIcon sx={{ color: 'error.main' }} />
        </Tooltip>
      );
    case 'INCOMPLETE':
      return (
        <Tooltip title="Incomplete">
          <span>
            <Text>Incomplete</Text>
          </span>
        </Tooltip>
      );
    case 'N_A':
    default:
      return (
        <Tooltip title="N/A">
          <span>
            <Text>Unverified</Text>
          </span>
        </Tooltip>
      );
  }
};

interface Props<DataT> {
  className?: string;
  /**
   * Title of project table
   */
  title?: string;
  /**
   * Text for the add button
   */
  addButtonText?: string;
  /**
   * Function to call when the add button is clicked
   */
  onAddButtonClick?: () => void;
  /**
   * Array of object containing information to render project table
   */
  data?: DataT[];
  /**
   * Used to extract a unique key for a given item at the specified index
   */
  keyExtractor?: (entry: DataT, index: number) => string;
  /**
   * Given an entry within the data array, render the project name
   */
  projectName?: (entry: DataT) => string | undefined;
  /**
   * Whether this entry is an in-progress creation-wizard draft (renders a
   * "Draft" chip next to the name).
   */
  isDraft?: (entry: DataT) => boolean;
  /**
   * Given an entry within the data array, render the risk level
   */
  risk?: (entry: DataT) => GQL.AssessmentRiskStatusType | undefined | null;
  /**
   * Given an entry within the data array, render the assessmentCompletion
   */
  assessmentCompletion?: (entry: DataT) => number | undefined | null;
  /**
   * Given an entry within the data array, return the project-level risk status
   * used to render the compliance badge in the PROJECT STATUS column. When
   * provided, the column is shown.
   */
  projectStatus?: (
    entry: DataT
  ) => GQL.AssessmentRiskStatusType | null | undefined;
  /**
   * Given an entry within the data array, render the policyAlignment
   */
  policyAlignment?: (entry: DataT) => boolean | undefined;
  /**
   * Given an entry within the data array, render the security assessment status
   */
  securityStatus?: (
    entry: DataT
  ) => 'N_A' | 'INCOMPLETE' | 'PASS' | 'FAIL' | undefined;
  /**
   * Given an entry within the data array, render the privacy assessment status
   */
  privacyStatus?: (
    entry: DataT
  ) => 'N_A' | 'INCOMPLETE' | 'PASS' | 'FAIL' | undefined;
  /**
   * Given an entry within the data array, render the bias assessment status
   */
  biasStatus?: (
    entry: DataT
  ) => 'N_A' | 'INCOMPLETE' | 'PASS' | 'FAIL' | undefined;
  /**
   * Given an entry within the data array, render the performance assessment status
   */
  performanceStatus?: (
    entry: DataT
  ) => 'N_A' | 'INCOMPLETE' | 'PASS' | 'FAIL' | undefined;
  /**
   * Given an entry within the data array, perform the action when clicked
   */
  onClick?: (entry: DataT) => void;
  /**
   * Given an entry within the data array, set the champion
   */
  onSetChampion?: (entry: DataT) => void;
  /**
   * Given an entry within the data array, get the project type
   */
  projectType?: (entry: DataT) => GQL.ProjectType | undefined;
  page: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (pageSize: number) => void;
  totalCount: number;
  onSortChange?: (direction: GQL.SortDirection | null) => void;
  sortDirection?: GQL.SortDirection | null;
  onRiskSortChange?: (filter: GQL.RiskFilter) => void;
  riskFilter?: GQL.RiskFilter;
  source?: (entry: DataT) => string | undefined;
  /**
   * When true, hide Assessment Completion and show Assessment Status columns
   * (Pre-Screening, Privacy, Safeguard) using workflow status.
   */
  showTrueAssessmentStatusColumns?: boolean;
  /**
   * Given an entry, return the assessmentStatusByPolicy array for that entry.
   */
  assessmentStatusByPolicy?: (
    entry: DataT
  ) => { policyIdentifier: string; status: GQL.AssessmentStatusType | null }[];
  /**
   * Whether to show the legend explaining verification statuses
   */
  showLegend?: boolean;
  /**
   * Optional tabs configuration - only renders tabs when provided
   */
  tabsConfig?: {
    tabs: { label: string; value: string }[];
    activeTab: string;
    onTabChange: (tab: string) => void;
    /** Content to render when a non-default tab is active */
    alternateTabContent?: React.ReactNode;
    /** Action to render in the card header for alternate tab */
    alternateTabAction?: React.ReactNode;
  };
  /**
   * Optional content to render inside the card, directly above the table (e.g. search bar)
   */
  searchSlot?: React.ReactNode;
  /**
   * Storage key for persisting view preference. Defaults based on title.
   */
  storageKey?: string;
  /**
   * Extract owner name string for card display
   */
  owner?: (entry: DataT) => string | undefined;
  /**
   * Extract description for card display
   */
  description?: (entry: DataT) => string | undefined;
}

type ProjectTableFn = <DataT>(
  props: Props<DataT>
) => ReturnType<React.FC<Props<DataT>>>;

const ProjectTableImpl: ProjectTableFn = ({
  className,
  title,
  addButtonText,
  onAddButtonClick,
  data = [],
  keyExtractor,
  projectName,
  isDraft,
  source,
  risk,
  assessmentCompletion,
  policyAlignment,
  securityStatus,
  privacyStatus,
  biasStatus,
  performanceStatus,
  onClick,
  projectType,
  onSetChampion,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  totalCount,
  onSortChange,
  sortDirection,
  onRiskSortChange,
  riskFilter,
  showTrueAssessmentStatusColumns,
  assessmentStatusByPolicy,
  projectStatus,
  showLegend,
  tabsConfig,
  searchSlot,
  storageKey,
  owner,
  description,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { view, ViewToggle } = useViewSwitcher({
    defaultView: 'table',
    persistView: true,
    storageKey: storageKey ?? `project-table-view-${title ?? 'default'}`,
  });
  // Lazy query for fetching all projects on export
  const [fetchAllProjects, { loading: fetchingAllProjects }] =
    GQL.useAiSystemSearchLazyQuery({
      fetchPolicy: 'network-only',
    });

  const {
    refetchDatasets,
    refetchFunctionalModels,
    refetchModels,
    refetchAgents,
    refetchAiSystemList,
  } = useAIResourcesContext();
  const { projectId, functionalModelId } = useParams();
  const [checkedMap, setCheckedMap] = React.useState<Record<number, boolean>>(
    {}
  );
  const handleChange =
    (idx: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setCheckedMap((prev) => ({ ...prev, [idx]: event.target.checked }));
    };

  const { userProfile: userProfile_ } = useUserProfile();
  const { hasCapability } = usePermissions();
  const Snackbar = useSnackbarContext();
  const [archiveProject, { loading: archiveProjectLoading }] =
    GQL.useArchiveProjectMutation({
      refetchQueries: [
        GQL.AiSystemListDocument,
        GQL.AiSystemDashboardDataDocument,
      ],
      awaitRefetchQueries: true,
    });
  const isAdmin =
    hasCapability('platform:manage_system_admins') ||
    hasCapability('project:archive');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const canUserArchiveProject = (project: any) => {
    if (isAdmin || hasCapability('project:archive')) {
      return true;
    }
    // project:archive is project-scoped; myPermissions only carries org-level roles,
    // so also check direct project ownership.
    return (
      project.sharedWith?.some(
        (member: any) =>
          member?.email === userProfile_?.email && member.role === 'Owner'
      ) ?? false
    );
  };

  // Determine the resource type based on the title
  const getResourceType = () => {
    if (title === 'DATASETS') {
      return 'Dataset';
    }
    if (title === 'AGENTS') {
      return 'Agent';
    }
    if (title === 'MODELS') {
      return 'Model';
    }
    return 'Project';
  };

  const archiveProjectHandler = (id: string) => {
    const resourceType = getResourceType();
    archiveProject({
      variables: { input: { projectId: id } },
      onCompleted: () => {
        refetchAiSystemList?.();
        refetchDatasets?.();
        refetchFunctionalModels?.();
        if (functionalModelId) {
          refetchModels?.();
          refetchAgents?.();
        }
      },
    })
      .then(() => {
        Snackbar.open(`${resourceType} deleted successfully.`, {
          showOk: true,
        });
      })
      .catch((err) => {
        console.error('Error archiving project:', err); // Log the error for debugging
        Snackbar.open(
          `Error deleting ${resourceType.toLowerCase()}: ${err.message || 'Unknown error'}`,
          {
            showOk: true,
          }
        );
      });
  };

  const handleExport = async () => {
    try {
      // Show loading state
      Snackbar.open('Preparing export...', { showOk: false });

      let allData = data;

      // If we have more data than what's on the current page, fetch all data
      if (totalCount > data.length) {
        // Use lazy query to fetch all projects with filters applied
        const result = await fetchAllProjects({
          variables: {
            pagination: {
              page: 0,
              pageSize: totalCount, // Fetch all at once
              riskFilter,
              sortDirection,
            },
          },
        });

        if (result.data?.aiSystemList?.projects) {
          // Map the fetched data to the same format as the current data
          allData = result.data.aiSystemList.projects.map((p) => ({
            id: p._id,
            projectName: p.name,
            source: p.source ?? 'Azure',
            risk: p.riskStatus ?? GQL.AssessmentRiskStatusType.High,
            assessmentCompletion: p.completionPercent ?? 0,
            policyAlignment: p.riskStatus ?? GQL.AssessmentRiskStatusType.High,
            numControls: p.numControls ?? 0,
            numTestScores: p.numTestScores ?? 0,
            securityStatus: p.securityStatus ?? GQL.AssessmentCategoryStatus.NA,
            privacyStatus: p.privacyStatus ?? GQL.AssessmentCategoryStatus.NA,
            biasStatus: p.biasStatus ?? GQL.AssessmentCategoryStatus.NA,
            performanceStatus:
              p.performanceStatus ?? GQL.AssessmentCategoryStatus.NA,
            assessmentStatusByPolicy: (p.assessmentStatusByPolicy ?? []).map(
              (s) => ({
                policyIdentifier: s.policyIdentifier,
                status: s.status ?? null,
              })
            ),
            sharedWith: (p.sharedWith ?? []).filter(
              (m): m is GQL.SharedMembers => m !== null
            ),
          })) as typeof data;
        }
      }

      const csvData = allData.map((entry) => ({
        name: projectName?.(entry) ?? '',
        source: source?.(entry) ?? '',
        residualRisk: risk?.(entry) ?? '',
        securityStatus: securityStatus?.(entry) ?? '',
        privacyStatus: privacyStatus?.(entry) ?? '',
        biasStatus: biasStatus?.(entry) ?? '',
        assessmentCompletion: assessmentCompletion?.(entry) ?? '',
        assessmentStatusByPolicy: assessmentStatusByPolicy?.(entry) ?? [],
        policyAlignment: policyAlignment?.(entry) ? 'Aligned' : 'Not Aligned',
      }));

      const today = new Date();
      const timestamp = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const titlename = title?.toLowerCase();

      exportToCsv(`${titlename}_data_${timestamp}.csv`, csvData, {
        showTrueAssessmentStatusColumns,
      });

      Snackbar.open(
        `Successfully exported ${csvData.length} ${title?.toLowerCase() ?? 'projects'}`,
        {
          showOk: true,
        }
      );
    } catch (error) {
      console.error('Error exporting data:', error);
      Snackbar.open('Failed to export data. Please try again.', {
        showOk: true,
      });
    }
  };

  const hasSourceData = data.some((entry) => source?.(entry) !== undefined);
  const hasRiskData = data.some((entry) => risk?.(entry) !== undefined);

  // Check if any entry has assessment category status data
  const hasSecurityData = data.some(
    (entry) => securityStatus?.(entry) !== undefined
  );
  const hasPrivacyData = data.some(
    (entry) => privacyStatus?.(entry) !== undefined
  );
  const hasBiasData = data.some((entry) => biasStatus?.(entry) !== undefined);
  const hasPerformanceData = data.some(
    (entry) => performanceStatus?.(entry) !== undefined
  );
  const hasAssuranceData =
    hasSecurityData || hasPrivacyData || hasBiasData || hasPerformanceData;
  const assuranceColSpan =
    (hasSecurityData ? 1 : 0) +
    (hasPrivacyData ? 1 : 0) +
    (hasBiasData ? 1 : 0) +
    (hasPerformanceData ? 1 : 0);
  const hasSubHeaders = hasAssuranceData || !!showTrueAssessmentStatusColumns;

  const numberOfColumns = calculateProjectTableColumns({
    hasSecurityData,
    hasPrivacyData,
    hasBiasData,
    hasPerformanceData,
    isAdmin: !!isAdmin,
  });

  const [riskAnchorEl, setRiskAnchorEl] = React.useState<null | HTMLElement>(
    null
  );

  const handleRiskFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setRiskAnchorEl(event.currentTarget);
  };

  const handleRiskFilterClose = () => {
    setRiskAnchorEl(null);
  };

  const handleRiskFilter = (filter: GQL.RiskFilter) => {
    onRiskSortChange?.(filter);
    handleRiskFilterClose();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    onRowsPerPageChange(newPageSize);
    onPageChange(0); // Reset to first page when changing page size
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const riskOptions = [
    { value: GQL.RiskFilter.High, label: 'High' },
    { value: GQL.RiskFilter.Medium, label: 'Medium' },
    { value: GQL.RiskFilter.Low, label: 'Low' },
  ];

  // Determine if we're showing alternate tab content
  const isShowingAlternateTab =
    tabsConfig && tabsConfig.activeTab !== tabsConfig.tabs[0]?.value;

  // Build title with optional tabs
  const cardTitle = tabsConfig ? (
    <Box className="tabs-title-wrapper">
      <Text sz="title">{title}</Text>
      <Tabs
        className="project-tabs"
        value={tabsConfig.activeTab}
        onChange={(_, v) => tabsConfig.onTabChange(v)}
      >
        {tabsConfig.tabs.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>
    </Box>
  ) : (
    title
  );

  // Build action - show alternate action when alternate tab is active
  const cardAction =
    isShowingAlternateTab && tabsConfig?.alternateTabAction ? (
      tabsConfig.alternateTabAction
    ) : (
      <Arranger
        direction="row"
        alignItems="center"
        sx={{ gap: '8px', mb: spc.s }}
      >
        <ViewToggle />
        {onAddButtonClick && (
          <Tooltip title={addButtonText}>
            <Button
              icon={AddCircleOutlineIcon}
              color="primary"
              size="large"
              onClick={onAddButtonClick}
            >
              Add
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Export">
          <Button
            icon={FileDownloadIcon}
            color="primary"
            size="large"
            onClick={handleExport}
            disabled={fetchingAllProjects}
          >
            Export
          </Button>
        </Tooltip>
      </Arranger>
    );

  // If showing alternate tab, render alternate content instead of table
  if (isShowingAlternateTab) {
    return (
      <Card className={className} title={cardTitle} action={cardAction}>
        {tabsConfig?.alternateTabContent}
      </Card>
    );
  }

  // Card view (explicit user choice, non-mobile)
  if (!isMobile && view === 'card') {
    return (
      <Card className={className} title={cardTitle} action={cardAction}>
        {searchSlot && <Box sx={{ mb: 2 }}>{searchSlot}</Box>}
        <Box className="entity-cards-grid">
          {data.map((entry, idx) => (
            <ProjectCard
              key={keyExtractor?.(entry, idx) ?? idx}
              entry={entry}
              projectName={projectName}
              source={source}
              risk={risk}
              assessmentCompletion={assessmentCompletion}
              policyAlignment={policyAlignment}
              owner={owner}
              description={description}
              securityStatus={securityStatus}
              privacyStatus={privacyStatus}
              biasStatus={biasStatus}
              performanceStatus={performanceStatus}
              showTrueAssessmentStatusColumns={showTrueAssessmentStatusColumns}
              assessmentStatusByPolicy={assessmentStatusByPolicy}
              isChampion={
                projectType
                  ? (e) => projectType(e) === GQL.ProjectType.ModelChampion
                  : undefined
              }
              onClick={onClick}
            />
          ))}
        </Box>
        {totalCount > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </Card>
    );
  }

  // Mobile view: render cards instead of table
  if (isMobile) {
    return (
      <Card className={className} title={cardTitle} action={cardAction}>
        {searchSlot && <Box sx={{ mb: 2 }}>{searchSlot}</Box>}
        <div className="cards-grid">
          {data.map((entry, idx) => (
            <ProjectCard
              key={keyExtractor?.(entry, idx) ?? idx}
              entry={entry}
              projectName={projectName}
              source={source}
              risk={risk}
              assessmentCompletion={assessmentCompletion}
              policyAlignment={policyAlignment}
              owner={owner}
              description={description}
              securityStatus={securityStatus}
              privacyStatus={privacyStatus}
              biasStatus={biasStatus}
              performanceStatus={performanceStatus}
              showTrueAssessmentStatusColumns={showTrueAssessmentStatusColumns}
              assessmentStatusByPolicy={assessmentStatusByPolicy}
              onClick={onClick}
            />
          ))}
        </div>
        {totalCount > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
        <Spacer pb={spc.s} />
        {showLegend && (
          <div className="table-legend">
            <Text sz="body12" color="textSecondary">
              <strong>Unverified:</strong> no verification assigned •{' '}
              <strong>Incomplete:</strong> verification assigned but have not
              been completed
            </Text>
          </div>
        )}
      </Card>
    );
  }

  // Tablet/Desktop view: table with scroll wrapper
  return (
    <Card className={className} title={cardTitle} action={cardAction}>
      {searchSlot && <Box sx={{ mb: 2 }}>{searchSlot}</Box>}
      <Box className="table-scroll-wrapper">
        <Table className="project-table">
          <TableHead>
            <TableRow>
              <StyledTableCell rowSpan={hasSubHeaders ? 2 : 1}>
                <Arranger direction="row" alignItems="center">
                  <Text sz="title">NAME</Text>
                  <SortButton
                    sortDirection={sortDirection ?? null}
                    onSortChange={onSortChange ?? (() => {})}
                  />
                </Arranger>
              </StyledTableCell>
              {title === 'AGENTS' && (
                <StyledTableCell rowSpan={hasSubHeaders ? 2 : 1}>
                  <Text sz="title">EAM</Text>
                </StyledTableCell>
              )}
              {hasSourceData && (
                <StyledTableCell rowSpan={hasSubHeaders ? 2 : 1}>
                  <Text sz="title">SOURCE</Text>
                </StyledTableCell>
              )}
              {hasRiskData && (
                <StyledTableCell rowSpan={hasSubHeaders ? 2 : 1}>
                  <Arranger direction="row" alignItems="center">
                    {' '}
                    <Text sz="title">RESIDUAL RISK</Text>
                    <IconButton
                      size="small"
                      onClick={handleRiskFilterClick}
                      sx={{ marginLeft: 1 }}
                    >
                      <FilterAltIcon />
                    </IconButton>
                  </Arranger>

                  <SortMenu
                    anchorEl={riskAnchorEl}
                    open={Boolean(riskAnchorEl)}
                    onClose={handleRiskFilterClose}
                    options={riskOptions}
                    selectedValue={riskFilter as GQL.RiskFilter}
                    onSelect={handleRiskFilter}
                    defaultValue={GQL.RiskFilter.All}
                  />
                </StyledTableCell>
              )}
              {hasAssuranceData && (
                <StyledTableCell colSpan={assuranceColSpan} align="center">
                  <Text sz="title">ASSURANCE STATUS</Text>
                </StyledTableCell>
              )}
              {showTrueAssessmentStatusColumns ? (
                <StyledTableCell colSpan={3} align="center">
                  <Text sz="title">ASSESSMENT STATUS</Text>
                </StyledTableCell>
              ) : (
                <StyledTableCell rowSpan={hasSubHeaders ? 2 : 1}>
                  <Text sz="title">ASSESSMENT COMPLETION</Text>
                </StyledTableCell>
              )}
              {projectStatus && (
                <StyledTableCell rowSpan={hasSubHeaders ? 2 : 1}>
                  <Text sz="title">PROJECT STATUS</Text>
                </StyledTableCell>
              )}
              <StyledTableCell rowSpan={hasSubHeaders ? 2 : 1}>
                <Text sz="title">ACTION</Text>
              </StyledTableCell>
            </TableRow>
            {hasSubHeaders && (
              <TableRow>
                {hasSecurityData && (
                  <StyledTableCell>
                    <Text sz="title">SECURITY</Text>
                  </StyledTableCell>
                )}
                {hasPrivacyData && (
                  <StyledTableCell>
                    <Text sz="title">PRIVACY</Text>
                  </StyledTableCell>
                )}
                {hasBiasData && (
                  <StyledTableCell>
                    <Text sz="title">BIAS</Text>
                  </StyledTableCell>
                )}
                {hasPerformanceData && (
                  <StyledTableCell>
                    <Text sz="title">PERFORMANCE</Text>
                  </StyledTableCell>
                )}
                {showTrueAssessmentStatusColumns &&
                  ASSESSMENT_STATUS_COLUMNS.map((col) => (
                    <StyledTableCell key={col.label}>
                      <Text sz="title">{col.label.toUpperCase()}</Text>
                    </StyledTableCell>
                  ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {data.map((entry, idx) => (
              <StyledTableRow
                key={keyExtractor?.(entry, idx) ?? idx}
                onClick={() => onClick?.(entry)}
                style={{ cursor: 'pointer' }}
              >
                <StyledTableCell>
                  <ProjectName>
                    <ChampionIcon
                      projectType={projectType?.(entry)}
                      onSetChampion={() => onSetChampion?.(entry)}
                    />
                    <Text>{projectName?.(entry) ?? ''}</Text>
                    {isDraft?.(entry) && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          // ProjectName is a flex row — without this the chip
                          // gets squeezed by long project names and the
                          // nowrap label spills outside its background.
                          flexShrink: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: 18,
                          px: 0.75,
                          borderRadius: '999px',
                          fontSize: '10px',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          lineHeight: 1,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          bgcolor: 'warning.main',
                          color: 'warning.contrastText',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Draft
                      </Box>
                    )}
                  </ProjectName>
                </StyledTableCell>
                {title === 'AGENTS' && (
                  <StyledTableCell onClick={(e) => e.stopPropagation()}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Switch
                        checked={!!checkedMap[idx]}
                        onChange={handleChange(idx)}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                      <Tooltip title="Enterprise agent monitoring">
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: 'text.secondary',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </StyledTableCell>
                )}
                {hasSourceData && (
                  <StyledTableCell>
                    <Text>{source?.(entry) ?? ''}</Text>
                  </StyledTableCell>
                )}
                {hasRiskData && (
                  <StyledTableCell>
                    <StatusBadge
                      variant="risk"
                      value={
                        risk?.(entry) === GQL.AssessmentRiskStatusType.High
                          ? 'high'
                          : risk?.(entry) ===
                              GQL.AssessmentRiskStatusType.Medium
                            ? 'medium'
                            : risk?.(entry) === GQL.AssessmentRiskStatusType.Low
                              ? 'low'
                              : 'unavailable'
                      }
                    />
                  </StyledTableCell>
                )}
                {hasSecurityData && (
                  <StyledTableCell>
                    <AssessmentCategoryStatus
                      status={securityStatus?.(entry)}
                    />
                  </StyledTableCell>
                )}
                {hasPrivacyData && (
                  <StyledTableCell>
                    <AssessmentCategoryStatus status={privacyStatus?.(entry)} />
                  </StyledTableCell>
                )}
                {hasBiasData && (
                  <StyledTableCell>
                    <AssessmentCategoryStatus status={biasStatus?.(entry)} />
                  </StyledTableCell>
                )}
                {hasPerformanceData && (
                  <StyledTableCell>
                    <AssessmentCategoryStatus
                      status={performanceStatus?.(entry)}
                    />
                  </StyledTableCell>
                )}
                {showTrueAssessmentStatusColumns ? (
                  (() => {
                    const statuses = assessmentStatusByPolicy?.(entry) ?? [];
                    return ASSESSMENT_STATUS_COLUMNS.map((col) => (
                      <StyledTableCell key={col.label}>
                        <Text>
                          {formatWorkflowStatus(
                            statuses[col.index]?.status ?? null
                          )}
                        </Text>
                      </StyledTableCell>
                    ));
                  })()
                ) : (
                  <StyledTableCell>
                    <ProgressWithText value={assessmentCompletion?.(entry)} />
                  </StyledTableCell>
                )}
                {projectStatus && (
                  <StyledTableCell>
                    <StatusBadge
                      variant="alignment"
                      value={toAlignmentLevel(projectStatus(entry))}
                    />
                  </StyledTableCell>
                )}
                <StyledTableCell>
                  <ProjectRowActions
                    className={className}
                    entry={entry}
                    canDelete={canUserArchiveProject(entry)}
                    onOpen={() => onClick?.(entry)}
                    onDelete={archiveProjectHandler}
                    deleteLoading={archiveProjectLoading}
                  />
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        <div className="scroll-hint">Swipe to see more columns</div>
      </Box>
      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      )}
      <Spacer pb={spc.s} />

      {/* Legend - only show for main project table */}
      {showLegend && (
        <div className="table-legend">
          <Text sz="body12" color="textSecondary">
            <strong>Unverified:</strong> no verification assigned •{' '}
            <strong>Incomplete:</strong> verification assigned but have not been
            completed
          </Text>
        </div>
      )}
    </Card>
  );
};

const ProjectTable = muiStyled(ProjectTableImpl)(
  ({ theme }) => css`
    .project-table {
      column-gap: ${theme.spacing(spc.xxxl)};
      align-items: center;
    }

    .champion-icon {
      margin-right: ${theme.spacing(spc.xs)};
      color: ${theme.palette.grey[400]};
      cursor: pointer;

      &.active {
        color: ${theme.palette.warning.main};
      }

      &:hover {
        color: ${theme.palette.warning.light};
      }
    }

    .table-legend {
      padding: ${theme.spacing(1)} 0;
      text-align: center;
      border-top: 1px solid ${theme.palette.divider};
      margin-top: ${theme.spacing(1)};

      span {
        color: ${theme.palette.grey[500]} !important;
        font-size: 0.75rem;
      }
    }

    .tabs-title-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: ${theme.spacing(1)};
    }

    .project-tabs {
      min-height: auto;

      .MuiTab-root {
        min-height: auto;
        padding: ${theme.spacing(0.5)} ${theme.spacing(2)};
        font-size: 13px;
        text-transform: none;
        color: ${theme.palette.text.secondary};

        &:first-of-type {
          padding-left: 0;
        }

        &.Mui-selected {
          color: ${theme.palette.primary.main};
        }
      }

      .MuiTabs-indicator {
        height: 2px;
      }
    }

    /* Desktop card grid (explicit card view toggle) */
    .entity-cards-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: ${theme.spacing(2)};
      padding: ${theme.spacing(1)} 0;
    }

    /* Mobile cards grid */
    .cards-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: ${theme.spacing(2)};
      width: 100%;
    }

    ${theme.breakpoints.down('sm')} {
      .cards-grid {
        grid-template-columns: 1fr;
      }
    }

    /* Table scroll wrapper for tablet */
    .table-scroll-wrapper {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .scroll-hint {
      display: none;
      text-align: center;
      padding: ${theme.spacing(1)} 0;
      font-size: 0.75rem;
      color: ${theme.palette.text.secondary};
      font-style: italic;
    }

    /* Show scroll hint on tablet sizes when table might overflow */
    ${theme.breakpoints.between('sm', 'lg')} {
      .scroll-hint {
        display: block;
      }
    }

    ${theme.breakpoints.up('lg')} {
      .scroll-hint {
        display: none;
      }
    }

    /* Add responsive spacing for small screens */
    @media (max-width: 768px) {
      .project-table {
        gap: ${theme.spacing(spc.between_vertical_s)}
          ${theme.spacing(spc.between_horizontal_s)} !important;
        overflow-x: auto;
      }

      .table-legend {
        font-size: 0.7rem;
        padding: ${theme.spacing(0.5)} 0;
      }
    }
  `
);

export default ProjectTable as ProjectTableFn;
